import React, { useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Crown, MicOff, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

const BALLOON_COLORS: Record<string, string> = {
  pink: "from-pink-400 to-rose-500",
  purple: "from-purple-400 to-violet-500",
  blue: "from-blue-400 to-cyan-500",
  amber: "from-amber-400 to-yellow-500",
  emerald: "from-emerald-400 to-green-500",
};

interface VideoBoothTileProps {
  name: string;
  photoUrl?: string;
  stream: MediaStream | null;
  isCurrentUser: boolean;
  isAdmin: boolean;
  isMuted: boolean;
  balloonNumber: number;
  balloonColor: string;
  popCount: number;
  canPop: boolean;
  popsRemaining: number;
  floatDelay: number;
  onPop: () => void;
  onExpand: () => void;
}

const VideoBoothTile: React.FC<VideoBoothTileProps> = ({
  name,
  photoUrl,
  stream,
  isCurrentUser,
  isAdmin,
  isMuted,
  balloonNumber,
  balloonColor,
  popCount,
  canPop,
  popsRemaining,
  floatDelay,
  onPop,
  onExpand,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [trackEnabled, setTrackEnabled] = useState(false);

  // Attach stream to video element
  useEffect(() => {
    const videoEl = videoRef.current;
    
    // Debug logging
    console.log("[VideoTile] Stream check:", {
      name,
      isCurrentUser,
      hasStream: !!stream,
      streamId: stream?.id,
      videoTracks: stream?.getVideoTracks().length,
    });

    if (!stream || !videoEl) {
      setVideoReady(false);
      setTrackEnabled(false);
      return;
    }

    // Attach stream
    videoEl.srcObject = stream;

    // Get initial track state
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      console.log("[VideoTile] Track state:", {
        enabled: videoTrack.enabled,
        readyState: videoTrack.readyState,
        muted: videoTrack.muted,
      });
      setTrackEnabled(videoTrack.enabled);
    }

    // Try to play
    videoEl.play()
      .then(() => {
        console.log("[VideoTile] Video playing successfully for", name);
        setVideoReady(true);
      })
      .catch(err => {
        console.error("[VideoTile] Play failed:", err);
        setVideoReady(false);
      });

    // Poll for track enabled state changes
    const checkTrack = () => {
      const track = stream.getVideoTracks()[0];
      if (track) {
        setTrackEnabled(track.enabled && track.readyState !== "ended");
      }
    };
    const interval = setInterval(checkTrack, 200);

    return () => {
      clearInterval(interval);
    };
  }, [stream, name, isCurrentUser]);

  // Show video if stream exists and track is enabled
  const showVideo = stream && videoReady && trackEnabled;

  console.log("[VideoTile] Render state:", { name, showVideo, videoReady, trackEnabled, hasStream: !!stream });

  return (
    <motion.div
      className="relative"
      animate={{
        y: [0, -6, 0],
      }}
      transition={{
        duration: 3 + Math.random(),
        repeat: Infinity,
        ease: "easeInOut",
        delay: floatDelay,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Square Video Container with rounded corners */}
      <div
        className={cn(
          "relative w-28 h-28 rounded-2xl overflow-hidden",
          "shadow-lg transition-transform duration-200",
          isHovered && "scale-105",
          isCurrentUser && "ring-2 ring-pink-500 ring-offset-2 ring-offset-background"
        )}
      >
        {/* Gradient Border Overlay */}
        <div className={cn(
          "absolute inset-0 rounded-2xl bg-gradient-to-br opacity-40 pointer-events-none z-0",
          BALLOON_COLORS[balloonColor] || BALLOON_COLORS.pink
        )} />

        {/* Video Container */}
        <div className="absolute inset-1 rounded-xl overflow-hidden bg-muted">
          {/* Video element - always render but hide if not showing */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={isCurrentUser}
            disablePictureInPicture
            className={cn(
              "w-full h-full object-cover absolute inset-0",
              showVideo ? "opacity-100 z-10" : "opacity-0 z-0"
            )}
            style={{ transform: isCurrentUser ? "scaleX(-1)" : undefined }}
          />
          
          {/* Avatar fallback - shown when video is not ready */}
          <div className={cn(
            "w-full h-full flex items-center justify-center bg-muted absolute inset-0",
            showVideo ? "opacity-0 z-0" : "opacity-100 z-10"
          )}>
            <Avatar className="w-14 h-14">
              <AvatarImage src={photoUrl} />
              <AvatarFallback className="text-lg bg-gradient-to-br from-pink-400 to-purple-500 text-white">
                {name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>

        {/* Balloon Number Badge */}
        <div className={cn(
          "absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shadow z-20",
          "bg-gradient-to-br",
          BALLOON_COLORS[balloonColor] || BALLOON_COLORS.pink
        )}>
          {balloonNumber}
        </div>

        {/* Admin Crown */}
        {isAdmin && (
          <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center z-20">
            <Crown className="w-3 h-3 text-white" />
          </div>
        )}

        {/* Muted Indicator */}
        {isMuted && (
          <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-red-500/80 flex items-center justify-center z-20">
            <MicOff className="w-3 h-3 text-white" />
          </div>
        )}

        {/* Pop Count */}
        {popCount > 0 && (
          <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded-full bg-pink-500 text-white text-[10px] font-bold z-20">
            💖 {popCount}
          </div>
        )}

        {/* Hover Actions */}
        {isHovered && !isCurrentUser && (
          <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-2 z-30">
            <Button
              size="sm"
              onClick={onPop}
              disabled={!canPop || popsRemaining <= 0}
              className="bg-pink-500 hover:bg-pink-600 text-white text-xs h-7 px-3"
            >
              {popsRemaining > 0 ? `Pop 💖` : "No pops left"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={onExpand}
              className="text-xs h-7 px-3"
            >
              <Maximize2 className="w-3 h-3 mr-1" />
              Expand
            </Button>
          </div>
        )}
      </div>

      {/* Name Label */}
      <div className="mt-2 text-center">
        <p className="text-xs font-medium truncate max-w-[100px] mx-auto">
          {isCurrentUser ? "You" : name}
        </p>
      </div>
    </motion.div>
  );
};

export default VideoBoothTile;
